/**
 * Onglet Couverture des shifts — grille jour × créneau, candidats
 * disponibles triés par score (le serveur trie déjà, cf. contrat). Une case
 * sans personne porte une pastille ROUGE « ⚠ Personne » (`Badge ton="rouge"`),
 * jamais un fond rouge écrit à la main.
 *
 * 01-11-2026 — au nouveau thème : un jour = un bloc, ses quatre créneaux en
 * cartes de la librairie (`Grille` : quatre en large, une colonne sur un
 * téléphone — plus de table à 820 px qui défile). Le créneau s'écrit dans sa
 * propre case, puisque l'en-tête de colonne n'existe plus.
 */
import { Badge, Carte, Etiquette, Grille, Ligne, Lignes, Section, Vide } from "@/shared/ui";

import { DAY_LABELS_FULL, REVIEW_THRESHOLD, SHIFTS } from "../constants";
import { formatPercent } from "../format";
import { DAY_KEYS, type CoverageMatrix } from "../types";

interface CoverageTabProps {
  matrix: CoverageMatrix | null;
  loading: boolean;
}

export function CoverageTab({ matrix, loading }: CoverageTabProps) {
  return (
    <Section
      titre="Couverture des shifts"
      sousTitre={
        <>
          Candidats disponibles, triés par score ·{" "}
          <span className="font-semibold text-erreur">rouge = personne</span>
        </>
      }
    >
      {loading && <Vide taille="sm" titre="Chargement…" />}
      {!loading && !matrix && <Vide taille="sm" titre="Couverture indisponible." />}

      {matrix && (
        <div className="flex flex-col gap-5">
          {DAY_KEYS.map((day) => (
            <div key={day} className="flex flex-col gap-2">
              <Etiquette as="h4">{DAY_LABELS_FULL[day]}</Etiquette>
              <Grille colonnes={4} ecart={8}>
                {SHIFTS.map((shift) => {
                  const cell = matrix[day]?.[shift.key] ?? [];
                  const empty = cell.length === 0;
                  return (
                    <Carte key={shift.key} remplissage="sm" className="flex flex-col gap-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <Etiquette>
                          {shift.letter} · {shift.name}
                        </Etiquette>
                        {!empty && (
                          <span className="shrink-0 text-[12px] text-faint">
                            {cell.length} dispo{cell.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      {empty ? (
                        <div>
                          <Badge ton="rouge" size="md">
                            ⚠ Personne
                          </Badge>
                        </div>
                      ) : (
                        <Lignes>
                          {cell.map((p) => (
                            <Ligne
                              key={p.candidate_id}
                              etiquette={p.first_name ?? "Candidat"}
                              valeur={
                                <span
                                  className={
                                    p.score === null
                                      ? "text-faint"
                                      : p.score < REVIEW_THRESHOLD
                                        ? "text-attention"
                                        : "text-valide"
                                  }
                                >
                                  {p.score === null ? "—" : formatPercent(p.score)}
                                </span>
                              }
                            />
                          ))}
                        </Lignes>
                      )}
                    </Carte>
                  );
                })}
              </Grille>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
